/**
 * My Trades headline P&L model (issue #258).
 *
 * The page shows ONE highlighted realized-P&L card, and its scope follows the
 * venue tab under the table: All folds both venues, Kalshi/Poly show that venue
 * alone. This module owns that projection so the page never invents a second
 * accounting recipe — it takes each venue's already-computed net P&L plus the
 * evidence of how trustworthy that number is, and returns exactly what the card
 * renders.
 *
 * Fail-closed is per venue: a venue with fee/settlement-incomplete rows, or one
 * that never loaded, yields a null amount rather than a partial total that looks
 * authoritative. Because each venue is judged on its own evidence, a dead
 * Polymarket feed can't withhold the Kalshi view (or vice versa).
 */

import { maskPnl } from '../../lib/pnlMask';

/** Which tab's scope the headline is showing. Card mode has no venue tabs, so
 *  the page passes 'all' there. */
export type HeadlineVenue = 'all' | 'kalshi' | 'polymarket';

/** One venue's realized-P&L evidence for the active date range + Closed/All
 *  filter. Both venues expose this shape already; nothing new is computed. */
export interface VenuePnlInput {
  /** Realized net P&L in cents, or null when it isn't computable. */
  netPnlCents: number | null;
  /** Closed rows folded into the amount. */
  closedCount: number;
  /** Closed rows whose P&L is still incomplete (missing fees/settlement).
   *  Any pending row makes the venue's total untrustworthy. */
  pendingCount: number;
  status: 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';
  /** Epoch ms of the last SUCCESSFUL fetch, or null if none — distinguishes
   *  "retained" (showing last-known rows) from "unavailable" (nothing ever). */
  lastSuccessMs: number | null;
}

export interface HeadlinePnl {
  label: string;
  /** null → the card renders '—'. */
  netPnlCents: number | null;
  sub: string;
}

/** Signed dollar amount with an explicit '+' on wins. Lives here because the
 *  headline is its primary consumer; the page imports it from here for its
 *  per-row P&L cells too. */
export function formatSignedDollars(cents: number): string {
  const sign = cents > 0 ? '+' : cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function isReady(v: VenuePnlInput): boolean {
  return v.status === 'ready' && v.pendingCount === 0 && v.netPnlCents != null;
}

/** The one-line state of a venue, in the same vocabulary as the freshness
 *  badge/banner: a failed refresh over prior rows reads "retained", never
 *  "unavailable" (#125). Kalshi never reports 'unavailable' — only Poly does,
 *  and for Poly that specifically means the venue isn't connected. */
function venueStatusPhrase(name: string, v: VenuePnlInput): string {
  if (v.status === 'ready') {
    return v.pendingCount > 0
      ? `${v.pendingCount} ${name} P&L pending`
      : `${v.closedCount} ${name} closed`;
  }
  if (v.status === 'idle' || v.status === 'loading') return `${name} loading`;
  if (v.status === 'unavailable') return `${name} not connected`;
  return v.lastSuccessMs != null ? `${name} retained` : `${name} unavailable`;
}

/** One venue's view: the label is pinned to the selected tab even while that
 *  venue is loading or retained, so the card's scope never silently disagrees
 *  with the table under it — readiness is carried by the amount and subtext. */
function venueHeadline(name: string, v: VenuePnlInput): HeadlinePnl {
  return {
    label: `Net P&L (${name})`,
    netPnlCents: isReady(v) ? v.netPnlCents : null,
    sub: venueStatusPhrase(name, v),
  };
}

export function selectHeadlinePnl({
  venue,
  kalshi,
  poly,
  hidePnl,
}: {
  venue: HeadlineVenue;
  kalshi: VenuePnlInput;
  poly: VenuePnlInput;
  hidePnl: boolean;
}): HeadlinePnl {
  if (venue === 'kalshi') return venueHeadline('Kalshi', kalshi);
  if (venue === 'polymarket') return venueHeadline('Polymarket', poly);

  // 'All' — fold both venues, but degrade to whichever venue IS trustworthy
  // rather than presenting a partial total under a combined label.
  const kalshiReady = isReady(kalshi);
  const polyReady = isReady(poly);
  if (kalshiReady && polyReady) {
    const kalshiCents = kalshi.netPnlCents ?? 0;
    const polyCents = poly.netPnlCents ?? 0;
    return {
      label: 'Net P&L (K + P)',
      netPnlCents: kalshiCents + polyCents,
      sub: `Kalshi ${maskPnl(hidePnl, formatSignedDollars(kalshiCents))} · Polymarket ${maskPnl(hidePnl, formatSignedDollars(polyCents))} · ${kalshi.closedCount + poly.closedCount} closed`,
    };
  }

  const sub = [
    venueStatusPhrase('Kalshi', kalshi),
    venueStatusPhrase('Polymarket', poly),
  ].join(' · ');

  if (kalshiReady) {
    return { label: 'Net P&L (Kalshi)', netPnlCents: kalshi.netPnlCents, sub };
  }
  if (polyReady) {
    return { label: 'Net P&L (Polymarket)', netPnlCents: poly.netPnlCents, sub };
  }
  return { label: 'Net P&L', netPnlCents: null, sub };
}
